import React, { useState } from "react";
import { Box, Typography, Tabs, Tab, CssBaseline, useMediaQuery, useTheme } from "@mui/material"; 
import { dataGridCustomizations } from "../../../theme/customizations";
import AppTheme from "../../../theme/AppTheme";
import DynamicNotificationForm from "./Fragments/DynamicNotificationForm";
import { notificationTabsConfig } from "./Config/notificationFormConfig";
import { systemAnnouncementValidationSchema } from "../Validation/buyerConfigValidation";
import { promotionalAnnouncementValidationSchema } from "../Validation/buyerConfigValidation";
import { SystemAnnouncementFormValues, PromotionalAnnouncementFormValues } from "../Types/BuyerConfigstype";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`notification-tabpanel-${index}`}
      aria-labelledby={`notification-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const PublicNotification: React.FC<{ disableCustomTheme?: boolean }> = (
  props
) => {
  const [activeTab, setActiveTab] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const xThemeComponents = {
    ...dataGridCustomizations,
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSuccess = () => {
    // Optionally reset form or show success message
    // Forms will handle their own success notifications
  };

  // Get initial values based on tab type
  const getInitialValues = (type: "system" | "promotion") => {
    if (type === "system") {
      return {
        title: "",
        message: "",
        type: "system" as const,
        image_url: "",
        data: {
          maintenance_date: "",
          start_time: "",
          end_time: "",
        },
      } as SystemAnnouncementFormValues;
    } else {
      return {
        title: "",
        message: "",
        type: "promotion" as const,
        image_url: "",
        data: {
          coupon_code: "",
          discount: "", // Empty string - will be validated and converted to number
          url: "",
        },
      } as PromotionalAnnouncementFormValues;
    }
  };

  // Get validation schema based on tab type
  const getValidationSchema = (type: "system" | "promotion") => {
    return type === "system"
      ? systemAnnouncementValidationSchema
      : promotionalAnnouncementValidationSchema;
  };

  return (
    <>
      <AppTheme {...props} themeComponents={xThemeComponents}>
        <CssBaseline enableColorScheme />
        <Box sx={{ width: "100%", maxWidth: { sm: "100%", md: "1700px" } }}>
          <Typography
            component="h2"
            variant="h6"
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Public Notification
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              border: 1,
              borderColor: "divider",
              backgroundColor: "white",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <Tabs
              orientation={isMobile ? "horizontal" : "vertical"}
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                borderRight: { md: 1 },
                borderBottom: { xs: 1, md: 0 },
                borderColor: "divider",
                minWidth: { md: 260 },
                width: { xs: "100%", md: "auto" },
                backgroundColor: "#fafafa",
                "& .MuiTabs-indicator": {
                  left: { md: 0 },
                  width: { md: 3 },
                  height: { xs: 3, md: "auto" },
                  backgroundColor: "#000",
                },
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  alignItems: { md: "flex-start" },
                  textAlign: { md: "left" },
                  justifyContent: { xs: "center", md: "flex-start" },
                  padding: { xs: "12px 8px", md: "14px 24px" },
                  minHeight: { xs: 48, md: 56 },
                  minWidth: { xs: "50%", md: "auto" },
                  flex: { xs: 1, md: "unset" },
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                  "&.Mui-selected": {
                    fontWeight: 600,
                    color: "#000",
                    backgroundColor: "rgba(0, 0, 0, 0.08)",
                  },
                },
              }}
            >
              {notificationTabsConfig.map((tab, index) => (
                <Tab key={index} label={tab.label} />
              ))}
            </Tabs>
            <Box sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, backgroundColor: "#ffffff" }}>
              {notificationTabsConfig.map((tab, index) => (
                <TabPanel key={index} value={activeTab} index={index}>
                  <DynamicNotificationForm
                    config={tab}
                    initialValues={getInitialValues(tab.type)}
                    validationSchema={getValidationSchema(tab.type)}
                    onSuccess={handleSuccess}
                  />
                </TabPanel>
              ))}
            </Box>
          </Box>
        </Box>
      </AppTheme>
    </>
  );
};

export default PublicNotification;